/**
 * Приглашение участника.
 *
 * Ссылка привязана к конкретному адресу и одноразова: ссылки «для всех
 * желающих» в продукте нет. Отсюда три разных исхода на одном экране, и путать
 * их нельзя.
 *
 * 1. **Учётной записи ещё нет** (`requires_signup`) — регистрация с токеном
 *    приглашения: одно действие закрывает и подтверждение почты, и приём.
 * 2. **Запись есть, человек вошёл** — принимаем, контекст сразу переключается
 *    на новую организацию.
 * 3. **Запись есть, но человек не вошёл** — сначала вход. Адрес подставляем,
 *    менять его бессмысленно: приглашение другому адресу не отдаётся.
 *
 * Что показывает превью, решает сервер: организацию слаг не выдаёт больше
 * нужного. Мы не дорисовываем к ней ничего своего.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AuthCard } from './AuthCard';
import { Field } from '../../ui/form';
import { Empty, ErrorNotice, Skeleton } from '../../ui/state';
import {
  acceptInvitation,
  resolveInvitation,
  signup,
  type InvitationPreview,
  type Session,
} from '../../api/auth';
import { ApiError, fieldErrors } from '../../api/problem';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, shortPassword } from '../../ui/password';
import { ROLE_NAMES, ROLE_SUMMARY } from '../../api/access';
import { dateProse } from '@shared/src/format';

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: InvitationPreview }
  | { kind: 'failed'; error: unknown };

export function Invite({
  signedIn,
  onDone,
}: {
  /** Вошедший принимает приглашение сразу; остальным сначала вход. */
  signedIn: boolean;
  onDone: (session: Session) => void;
}) {
  const { token = '' } = useParams();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  // Регистрация прошла — ушло письмо. Сессии ещё нет и быть не может.
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const abort = new AbortController();
    resolveInvitation(token, abort.signal)
      .then((preview) => setLoad({ kind: 'ready', preview }))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoad({ kind: 'failed', error: caught });
      });
    return () => abort.abort();
  }, [token]);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      onDone(await acceptInvitation(token));
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function register(event: FormEvent) {
    event.preventDefault();
    if (load.kind !== 'ready') return;
    setBusy(true);
    setError(null);
    setFields({});
    try {
      // Регистрация отвечает `202` и письмом, а не сессией: войти сразу
      // нельзя, пока адрес не подтверждён. Раньше здесь стояло
      // `onDone(await signup(...))` — то есть в кабинет заходили с `undefined`
      // вместо сессии.
      await signup({
        email: load.preview.email,
        password,
        full_name: fullName,
        invitation_token: token,
      });
      setSent(true);
    } catch (caught) {
      if (caught instanceof ApiError) setFields(fieldErrors(caught.problem));
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (load.kind === 'loading') {
    return (
      <AuthCard title="Открываем приглашение">
        <Skeleton rows={2} />
      </AuthCard>
    );
  }

  if (load.kind === 'failed') {
    const gone = load.error instanceof ApiError && load.error.problem.status === 410;
    return (
      <AuthCard title="Приглашение недоступно">
        <Empty
          eyebrow={gone ? 'Срок вышел' : 'Ссылка не найдена'}
          title={gone ? 'Это приглашение истекло' : 'Такого приглашения нет'}
          action={
            <Link className="btn btn-ghost" to="/auth/login">
              Перейти ко входу
            </Link>
          }
        >
          Приглашения живут ограниченный срок и гаснут после приёма. Попросите владельца
          организации прислать новое — на тот же адрес.
        </Empty>
      </AuthCard>
    );
  }

  const { preview } = load;
  const lead = (
    <>
      {preview.inviter_name ? `${preview.inviter_name} приглашает вас` : 'Вас приглашают'}{' '}
      в организацию «{preview.organization_name}»
      {preview.workspace_name ? `, пространство «${preview.workspace_name}»` : ''}.
    </>
  );

  const details = (
    <dl className="invite-facts">
      <div>
        <dt>Роль</dt>
        <dd>
          {ROLE_NAMES[preview.role]}
          <span className="stamp">{ROLE_SUMMARY[preview.role]}</span>
        </dd>
      </div>
      <div>
        <dt>Адрес</dt>
        <dd>{preview.email}</dd>
      </div>
      <div>
        <dt>Действует до</dt>
        <dd>{dateProse(preview.expires_at)}</dd>
      </div>
    </dl>
  );

  // Письмо ушло. Дальше человек идёт по ссылке из него — «/auth/verify»
  // подтверждает адрес и сразу выдаёт сессию, второго шага «теперь войдите» у
  // нас нет.
  if (sent) {
    return (
      <AuthCard title="Проверьте почту">
        <p className="auth-lead">
          Мы отправили письмо на <b>{preview.email}</b>. Откройте ссылку из него — она
          подтвердит адрес и сразу впустит вас в организацию.
        </p>
        <p className="auth-lead">
          Письма нет через пару минут — посмотрите в спаме. Ссылка действует до{' '}
          {dateProse(preview.expires_at)}.
        </p>
      </AuthCard>
    );
  }

  // 1. Записи ещё нет — регистрация вместе с приёмом.
  if (preview.requires_signup) {
    return (
      <AuthCard title="Приглашение в Seeno" lead={lead}>
        {details}
        {error ? <ErrorNotice error={error} /> : null}

        <form onSubmit={register} noValidate>
          <Field
            id="full-name"
            label="Как вас зовут"
            hint="Имя видят коллеги по организации"
          >
            {(props) => (
              <input
                {...props}
                type="text"
                name="full_name"
                autoComplete="name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            )}
          </Field>

          <Field
            id="password"
            label="Пароль"
            hint={PASSWORD_HINT}
            error={fields.password ?? shortPassword(password)}
          >
            {(props) => (
              <input
                {...props}
                type="password"
                name="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={busy || password.length < PASSWORD_MIN_LENGTH}
          >
            {busy ? 'Создаём…' : 'Принять и создать учётную запись'}
          </button>
        </form>
      </AuthCard>
    );
  }

  // 2. Вошёл — принимаем.
  if (signedIn) {
    return (
      <AuthCard title="Приглашение в Seeno" lead={lead}>
        {details}
        {error ? <ErrorNotice error={error} /> : null}

        <button
          className="btn btn-primary"
          type="button"
          onClick={() => void accept()}
          disabled={busy}
        >
          {busy ? 'Принимаем…' : 'Принять приглашение'}
        </button>
        <div className="auth-foot">
          <p className="micro">
            Приглашение отдаётся только адресу {preview.email}. Если вы вошли под другим —
            выйдите и войдите нужным.
          </p>
        </div>
      </AuthCard>
    );
  }

  // 3. Запись есть, но человек не вошёл.
  return (
    <AuthCard title="Приглашение в Seeno" lead={lead}>
      {details}
      <p className="auth-lead">
        Такая учётная запись у нас уже есть. Войдите под {preview.email} — и приглашение
        примется.
      </p>
      <Link
        className="btn btn-primary"
        to="/auth/login"
        state={{ from: `/invite/${token}` }}
      >
        Войти и принять
      </Link>
    </AuthCard>
  );
}
